import React, { useState } from 'react'
import { 
  CheckCircleIcon, 
  EnvelopeIcon, 
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'

const SignupSuccessScreen = ({ email, onBackToLogin, onResendConfirmation }) => {
  const [resending, setResending] = useState(false)

  const handleResendConfirmation = async () => {
    if (resending) return
    
    setResending(true)
    try {
      if (onResendConfirmation) {
        const { error } = await onResendConfirmation(email)
        if (error) {
          toast.error('Failed to resend confirmation email')
        } else {
          toast.success('Confirmation email sent!')
        }
      }
    } catch (error) {
      toast.error('Failed to resend confirmation email')
    } finally {
      setResending(false)
    }
  }

  const handleBackToLogin = () => {
    if (onBackToLogin) {
      onBackToLogin()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
            <CheckCircleIconSolid className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Account Created Successfully!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Welcome to E8 Tracker
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          
          {/* Account Info */}
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Account Created
                </h3>
                <p className="mt-1 text-sm text-green-700">
                  Your E8 Tracker account has been created for <strong>{email}</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 text-center">
              What happens next?
            </h3>
            
            {/* Step 1 - Email Verification */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100">
                  <EnvelopeIcon className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h4 className="text-sm font-medium text-gray-900">
                  Step 1: Check Your Email
                </h4>
                <p className="mt-1 text-sm text-gray-600">
                  We've sent a verification link to your email address. Click the link to verify your account.
                </p>
                <button
                  onClick={handleResendConfirmation}
                  disabled={resending}
                  className="mt-2 text-xs text-blue-600 hover:text-blue-500 disabled:opacity-50 flex items-center"
                >
                  {resending ? (
                    <>
                      <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Didn\'t receive it? Resend email'
                  )}
                </button>
              </div>
            </div>

            {/* Step 2 - Access Application */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-100">
                  <CheckCircleIcon className="h-4 w-4 text-green-600" />
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h4 className="text-sm font-medium text-gray-900">
                  Step 2: Start Using E8 Tracker
                </h4>
                <p className="mt-1 text-sm text-gray-600">
                  After email verification, you can immediately sign in and access all E8 Tracker features.
                </p>
              </div>
            </div>
          </div>

          {/* Timeline removed per requirements */}

          {/* Support Info */}
          <div className="mt-6 text-center text-xs text-gray-500">
            <p>
              Need help or have questions?
            </p>
            <p className="mt-1">
              Contact support: <span className="font-medium text-gray-700">support@e8tracker.au</span>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 space-y-3">
            <button
              onClick={handleBackToLogin}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to Sign In
            </button>
            
            <div className="text-center">
              <p className="text-xs text-gray-500">
                You can close this page and return when you're ready to sign in
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignupSuccessScreen